import axios from 'axios';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { platformService } from '../../services/platform.service';
import { ShrinkStep, alignmentMargins, catalogImageId, catalogImageMarker, createCatalogImageResolver, fitCatalogImage, imageWidthCss, parseImageTitle, placeholderLayout } from './catalogImages';

export const catalogImageResolver = createCatalogImageResolver(
  (id: string, thumbnail: boolean, ticketId?: number) => new platformService().getCatalogImage(id, thumbnail, ticketId),
  (picture: Blob) => URL.createObjectURL(picture)
);

export class CatalogImageUploadError extends Error {}

const measureImage = async (file: Blob) => {
  const bitmap = await createImageBitmap(file);
  const size = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return size;
};

const encodeImage = async (file: Blob, step: ShrinkStep, height: number): Promise<Blob | null> => {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = step.width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  // JPEG has no transparency; without a fill, transparent areas come out black.
  if (step.type === 'image/jpeg') {
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, step.width, height);
  }
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, 0, 0, step.width, height);
  bitmap.close();
  return new Promise(resolve => canvas.toBlob(resolve, step.type, step.quality));
};

/** The picture to upload, shrunk in the browser if it is over the cap - or why it cannot be added. */
export const prepareCatalogImage = (file: Blob) => fitCatalogImage(file, measureImage, encodeImage);

/**
 * Uploads a picture and returns its marker for the answer, plus a URL to show it with. Pass the URL the
 * picture is already being shown with, so it is remembered rather than fetched back.
 */
export const uploadCatalogImage = async (file: Blob, fileName: string, shownAs?: string): Promise<{ marker: string; displayUrl: string }> => {
  try {
    const { id } = await new platformService().uploadCatalogImage(file, fileName);
    const displayUrl = shownAs ?? URL.createObjectURL(file);
    catalogImageResolver.remember(id, displayUrl);
    return { marker: catalogImageMarker(id), displayUrl };
  } catch (e: unknown) {
    // Only a refusal (400) carries a reason for the person adding the picture: a code - empty, size, type, folder -
    // that the editor turns into a message in their language. Anything else - a server fault, no connection - would
    // show its technical text, so it gets a plain message instead.
    const reason = axios.isAxiosError(e) && e.response?.status === 400 ? e.response.data : null;
    throw new CatalogImageUploadError(typeof reason === 'string' && reason ? reason : i18next.t('knowledge.add-knowledge-modal.image-upload-error'));
  }
};

/**
 * Gives back images that were uploaded and then let go of, so they do not sit in SharePoint unused. Best effort, and
 * never rejects; await it only where the page is about to disappear (a Teams pop-up closing). The server keeps any
 * image a catalog item still uses, and a failure only leaves a file behind.
 */
export const discardCatalogImages = (ids: string[]): Promise<void> => {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (unique.length === 0) return Promise.resolve();
  return new platformService().discardCatalogImages(unique).catch(() => { /* left in SharePoint */ });
};

/**
 * The same hand-back for a page that is being torn down - a tab closed, or a Teams pop-up shut with its own X or Esc,
 * which ends the page without running any of its buttons. An ordinary request is cancelled with the page;
 * `keepalive` lets this one finish. Built by hand rather than through platformService, because axios cannot ask for
 * keepalive. The headers are the ones platformService sends.
 */
export const discardCatalogImagesOnLeave = (ids: string[]) => {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (unique.length === 0) return;
  const token = localStorage.getItem('token') || localStorage.getItem(`msal.${window.__runtimeConfig.msalClientId}.idtoken`);
  fetch(`${window.__runtimeConfig.platformurl}catalogimages/discard`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Bearer ${token}`, 'x-tikit-tid': localStorage.getItem('tid') ?? '' },
    body: JSON.stringify(unique)
  }).catch(() => { /* left in SharePoint */ });
};

/**
 * Hands back `uploaded` when the page is torn down rather than left through its own buttons, then empties it so
 * nothing is handed back twice.
 */
export const useDiscardOnLeave = (uploaded: React.MutableRefObject<string[]>) => {
  useEffect(() => {
    const leave = () => {
      discardCatalogImagesOnLeave(uploaded.current);
      uploaded.current = [];
    };
    window.addEventListener('pagehide', leave);
    return () => window.removeEventListener('pagehide', leave);
  }, []);
};

/**
 * An `img` for Markdown renderers. A pasted catalog picture is fetched with the user's token;
 * anything else is an ordinary image and is left alone. A picture the viewer may not see comes
 * back 404 and renders as nothing, the same as one that no longer exists. Either kind holds its
 * space, shimmering, until the browser has drawn it - a picture linked from a web page popped in
 * late and moved the text under it.
 */
/** A Markdown renderer's `img` props, plus what the ticket and catalog views pass with them. */
export interface CatalogImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** The Markdown node react-markdown passes; never reaches the `img`. */
  node?: unknown;
  /** Fetch the small version, for lists. */
  thumbnail?: boolean;
  /** The ticket a deflected answer's picture belongs to. */
  ticketId?: number;
}

export const CatalogImage = (props: CatalogImageProps) => {
  const { node, src, alt, thumbnail, title, style, ticketId, ...rest } = props;
  const id = catalogImageId(src);
  const [resolved, setResolved] = useState<string | null | undefined>(id ? undefined : src);
  const [drawn, setDrawn] = useState(false);
  useEffect(() => setDrawn(false), [resolved]);

  useEffect(() => {
    if (!id) {
      setResolved(src);
      return;
    }
    let cancelled = false;
    setResolved(undefined);
    catalogImageResolver.resolve(id, !!thumbnail, ticketId)
      .then(url => { if (!cancelled) setResolved(url); })
      .catch(() => { if (!cancelled) setResolved(null); });
    return () => { cancelled = true; };
  }, [id, src, thumbnail, ticketId]);

  if (resolved === undefined) return <span className="catalog-image-placeholder" role="img" aria-label={alt ?? ''} aria-busy="true" style={{ ...placeholderLayout(title), ...style }} />;
  if (!resolved) return null;
  // A broken link counts as drawn, so it shows as it did before rather than shimmering for good.
  const img = <img src={resolved} alt={alt ?? ''} style={{ ...imageLayout(title), ...style, ...(drawn ? {} : { display: 'none' }) }}
    ref={el => { if (!drawn && el?.complete) setDrawn(true); }} onLoad={() => setDrawn(true)} onError={() => setDrawn(true)} {...rest} />;
  if (drawn) return img;
  return <>
    <span className="catalog-image-placeholder" role="img" aria-label={alt ?? ''} aria-busy="true" style={{ ...placeholderLayout(title), ...style }} />
    {img}
  </>;
};

/**
 * The Markdown title carries the editor's alignment and width (`"left 400px"`), not a caption, so it becomes
 * layout rather than a tooltip. A thumbnail passes its own size in `style`, which wins.
 */
const imageLayout = (title?: string): React.CSSProperties => {
  const layout = parseImageTitle(title);
  const width = imageWidthCss(layout);
  const { alignment } = layout;
  if (!alignment && !width) return {};
  return {
    display: 'block',
    maxWidth: '100%',
    ...(width ? { width } : {}),
    ...alignmentMargins(alignment)
  };
};
